import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { isMobileOnly } from "react-device-detect";
import { FiChevronUp } from "react-icons/fi";
import ScrollToTop from "./common/ScrollToTop";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import slugify from "slugify";
import ColumnGallery from "./common/ColumnGallery";
import { DataContext } from "../context/DataContext";
import { siteUrl, chapterNames } from "../utils/constants";
import { parseStorageItem, setStorageItem } from "../utils/funcs";
import { CaseEntity } from "../types/strapi";
import { isCaseEntity } from "../utils/caseDetailsUtils";
import PageHelmet from "./common/Helmet";
import Header from "./common/Header";
import BreadcrumbCases from "./common/BreadcrumbCases";
import ChapterButtons from "./common/ChapterButtons";
import FooterTwo from "./common/FooterTwo";
import MapTwo from "./objects/MapTwo";
import TreemapChart from "./objects/TreemapChart";
import EvidenceExplorerBlock from "./objects/EvidenceExplorerBlock";
import EvidenceFindingsBlock from "./objects/EvidenceFindingsBlock";
import LexiconPanel from "./objects/LexiconPanel";
import VideoModal from "./objects/VideoModal";
import VennDiagramComponent from "./objects/VennDiagram";
import TextBlock from "./objects/TextBlock";
import Video from "./objects/Video";
import Image from "./objects/Image";
import Images from "./objects/Images";
import TextBlockTwo from "./objects/TextBlockTwo";
import AudioFile from "./objects/AudioFile";
import Tabs from "./objects/Tabs";
import Timeline from "./objects/Timeline";
import BackgroundSection from "./objects/BackgroundSection";
import ImageTextColumns from "./objects/ImageTextColumns";
import { mediaUrl } from "../utils/imageUtils";

interface CaseDetailsProps {
  setRedirectToCases: (value: boolean) => void;
}

const CaseDetails: React.FC<CaseDetailsProps> = ({ setRedirectToCases }) => {
  const dataContext = useContext(DataContext);

  if (!dataContext) {
    throw new Error("CaseDetails must be wrapped with DataProvider");
  }

  const { videosVimeo, cases, videos, locale } = dataContext;
  const casePath = window?.location?.pathname?.replace("/cases/", "");

  const findCaseBySlug = useCallback((casesList: any, targetSlug: string) => {
    if (!casesList || !Array.isArray(casesList)) {
      return null;
    }

    let caseData = casesList.find(
      (item) =>
        item.name &&
        slugify(item.name.toLowerCase()) === targetSlug
    );

    if (!caseData) {
      caseData = casesList.find((item) => {
        if (!item.localizations) {
          return false;
        }

        return item.localizations.some(
          (localization: any) =>
            localization.name &&
            slugify(localization.name.toLowerCase()) === targetSlug
        );
      });
    }

    return caseData;
  }, []);

  const [activeContainer, setActiveContainer] = useState(0);
  const [caseName, setCaseName] = useState<CaseEntity | null>(() => {
    const storedCase = parseStorageItem("caseName");
    return isCaseEntity(storedCase) ? (storedCase as unknown as CaseEntity) : null;
  });
  const [data, setData] = useState(parseStorageItem("data") || []);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [caseError, setCaseError] = useState<string | null>(null);
  const [toTop, setToTop] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [selectedVideoDescription, setSelectedVideoDescription] =
    useState(null);

  // The context list carries no chapter* fields (they are fetched per case
  // below), so the slug is resolved against the list — which still has every
  // case's name and localizations — and only the matched case's chapters are
  // fetched, by documentId.
  useEffect(() => {
    setActiveContainer(0);

    if (!cases || !Array.isArray(cases) || cases.length === 0) {
      return undefined;
    }

    const listEntry = findCaseBySlug(cases, casePath);

    // Only a list that has loaded and genuinely has no match is a 404. A
    // detail fetch that is merely still in flight must not redirect.
    if (!listEntry) {
      setRedirectToCases(true);
      return undefined;
    }

    const documentId = listEntry.documentId ?? listEntry.id;
    let cancelled = false;

    setIsCaseLoading(true);
    setCaseError(null);

    // persist=false for the chapter-less list entry: sessionStorage seeds the
    // initial state on reload, and caching a case without chapters there would
    // paint an empty case body on the next load.
    const applyCase = (caseData: any, persist: boolean) => {
      setCaseName(caseData);

      const containerData = caseData || [];
      setData(containerData);

      if (persist) {
        setStorageItem("caseName", caseData);
        setStorageItem("data", containerData);
      }

      const mapDataArray = caseData?.map;
      if (mapDataArray?.length) {
        setStorageItem("caseMapData", mapDataArray);

        const userChapters = mapDataArray.map(
          ({ pitch, longitude, latitude, zoom }: { pitch: number, longitude: number, latitude: number, zoom: number }) => ({
            pitch,
            duration: 6000,
            center: [longitude, latitude],
            zoom,
          })
        );

        setStorageItem("caseMapChapters", userChapters);
      }
    };

    // Render the list entry immediately (title, background image) so the page
    // is not blank while the chapters are on the wire.
    applyCase(listEntry, false);

    const chapterPopulate = chapterNames
      .map((chapter) => `populate[${chapter}][populate]=*`)
      .join("&");

    axios
      .get(
        `${siteUrl}/api/cases/${documentId}?locale=${locale}&${chapterPopulate}&populate[backgroundImage][populate]=*&populate[localizations][populate]=*`
      )
      .then((response) => {
        if (cancelled) {
          return;
        }

        const detail = response?.data?.data;
        if (detail) {
          // The detail response is fetched at the same locale and is
          // authoritative for the chapters; the list entry backfills anything
          // the detail query did not populate.
          applyCase({ ...listEntry, ...detail }, true);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        // The list entry is already rendered; keep it rather than bouncing the
        // reader to /cases over a failed chapter fetch.
        setCaseError(
          error instanceof Error ? error.message : "Failed to load case"
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsCaseLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cases, casePath, setRedirectToCases, locale, findCaseBySlug]);

  const activateContainer = useCallback((containerId: number) => {
    setActiveContainer(containerId);
    sessionStorage.setItem("activeChapter", String(containerId));
  }, []);

  const imgUrl = useMemo(() => {
    if (!caseName?.backgroundImage) {
      return "";
    }

    const backgroundImage = caseName.backgroundImage;

    if (isMobileOnly && backgroundImage.formats?.small?.url) {
      return backgroundImage.formats.small.url;
    }

    return backgroundImage.url || "";
  }, [caseName]);

  const container = useMemo(
    () => data[chapterNames[activeContainer]],
    [data, activeContainer]
  );

  // The evidence-explorer chapter keeps its state across chapter flips: it
  // renders persistently (hidden when another chapter is active) instead
  // of unmounting, so a reader's exploration — mode, filters, pinned term
  // — survives a detour through the other chapters. Mounted lazily on
  // first visit so opening the case doesn't fetch evidence data.
  const explorerChapter = useMemo(() => {
    for (let i = 0; i < chapterNames.length; i += 1) {
      const items = data[chapterNames[i]];
      const block = Array.isArray(items)
        ? items.find(
          (it: { __component?: string } | null) =>
            it?.__component === "element.evidence-explorer")
        : undefined;
      if (block) return { index: i, heading: block.heading as string };
    }
    return null;
  }, [data]);
  const [explorerVisited, setExplorerVisited] = useState(false);
  useEffect(() => {
    if (explorerChapter && activeContainer === explorerChapter.index) {
      setExplorerVisited(true);
    }
  }, [explorerChapter, activeContainer]);

  // The map block may sit anywhere in its chapter (an intro text can
  // precede it) — find it rather than assuming first position.
  const mapBlock = useMemo(
    () => (Array.isArray(container)
      ? container.find(
        (it: { __component?: string } | null) =>
          it?.__component === "element.map")
      : undefined),
    [container]
  );

  const containerCssClasses = useMemo(() => {
    const isMap = !!mapBlock;
    const isD3js = container?.__component === "element.d3js";

    const baseClass = isMap
      ? "pt--20 pb--70"
      : "rn-blog-details pt--20 pb--70 bg_color--1";

    const darkClass = isD3js ? "" : "";

    return { baseClass, darkClass };
  }, [container, mapBlock]);

  const vimeoImageMap = useMemo(() => {
    const imageIndex = isMobileOnly ? 4 : 5;
    const imageMap = {};

    videosVimeo.forEach((video) => {
      const videoId = video?.uri?.replace("/videos/", "");
      imageMap[videoId] = video.pictures.sizes[imageIndex].link;
    });

    return imageMap;
  }, [videosVimeo]);

  const filteredVideos = useMemo(() => {
    if (!videos || !caseName?.name) {
      return [];
    }

    return videos.filter((video) => {
      const showcaseName =
        video?.contentType?.showcase?.name;
      return (
        showcaseName?.toLowerCase() === caseName.name.toLowerCase()
      );
    });
  }, [videos, caseName]);

  const photos = useMemo(
    () =>
      filteredVideos
        .map((video) => {
          const imageData =
            video?.contentType?.image;

          if (!imageData) {
            return null;
          }

          return {
            key: video.id.toString(),
            description: video?.contentType?.description,
            height: imageData.height,
            src: mediaUrl(imageData.url),
            url: video.contentType.url,
            width: imageData.width,
          };
        })
        .filter(Boolean),
    [filteredVideos]
  );

  const handleImageClick = useCallback((event, { photo }) => {
    setSelectedImage(photo.src);
    setSelectedVideoUrl(photo.url);
    setSelectedVideoDescription(photo.description);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setSelectedVideoDescription(null);
  }, []);

  const renderComponent = useCallback(
    (item, index) => {
      if (!item) {
        return null;
      }

      let vidImg;
      let audioFileUrl;

      switch (item.__component) {
        case "element.heading":
          return item?.heading ? (
            <TextBlock
              key={index}
              caseTitle={item.heading}
              cssClass="mt--100"
            />
          ) : null;

        case "element.video":
          vidImg =
            item.url?.indexOf("https://vimeo.com/") !== -1
              ? vimeoImageMap[item?.url?.replace("https://vimeo.com/", "")]
              : "";
          return (
            <Video
              key={index}
              type={item.type ?? ""}
              videoData={item}
              vidImg={vidImg}
            />
          );

        case "element.image":
          return item?.image ? (
            <Image
              key={index}
              type={item.type ?? ""}
              url={mediaUrl(item.image.url)}
              caption={item.caption || ""}
            />
          ) : null;

        case "element.image-collection":
          return item?.image ? (
            <Images
              key={index}
              type={item.type}
              images={item.image}
              toTop
            />
          ) : null;

        case "element.text-block":
          return item?.textBlock ? (
            <TextBlockTwo key={index} content={item.textBlock} />
          ) : null;

        case "element.preamble":
          return item.preamble ? (
            <div key={index} className="container">
              <div className="row">
                <div className="col-lg-12">
                  <div className="inner-wrapper">
                    <div className="inner">
                      <blockquote className="rn-blog-quote">
                        <BlocksRenderer content={item.preamble} />
                      </blockquote>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null;

        case "element.audio-file":
          audioFileUrl = item?.audioFile?.url
            ? mediaUrl(item.audioFile.url)
            : null;

          return audioFileUrl ? (
            <div key={index} className="container">
              <div className="row">
                <div className="col-lg-12">
                  <div className="inner-wrapper">
                    <div className="inner mt-xl-3 mb-xl-5">
                      <AudioFile url={audioFileUrl} />
                      <p className="text-center mt-3">
                        {item?.audioFile?.caption}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null;

        case "element.video-gallery":
          return (
            <div key={index}>
              <ColumnGallery
                photos={photos}
                onClick={handleImageClick}
                margin={1}
              />
            </div>
          );

        case "element.circles":
          return item?.circles ? (
            <VennDiagramComponent key={index} circles={item.circles} />
          ) : null;

        case "element.d3js":
          return <TreemapChart key={index} container={item} />;

        case "element.evidence-explorer":
          // Rendered persistently outside this map (state survives chapter
          // flips) — see explorerChapter below.
          return null;

        case "element.evidence-findings":
          return (
            <EvidenceFindingsBlock
              key={index}
              heading={item.heading}
              set={item.set || undefined}
              compareSet={item.compareSet || undefined}
              panel={item.panel || undefined}
            />
          );

        case "element.lexicon-panel":
          return (
            <LexiconPanel key={index} heading={item.heading}
              set={item.set || undefined} />
          );

        case "element.tabs-group":
          return <Tabs key={index} items={item.entries} />;

        case "element.timeline-group": {
          const { superHeading, heading } = item;
          return (
            <div key={index} className="rn-counterup-area ptb--120 bg_color--5">
              <div className="container">
                <div className="row">
                  {(superHeading || heading) && (
                    <div className="col-lg-12">
                      <div className="section-title text-center">
                        {superHeading && <span className="subtitle">{superHeading}</span>}
                        {heading && <h3 className="title">{heading}</h3>}
                      </div>
                    </div>
                  )}
                  <Timeline
                    timeline={item.entries.map((i: any) => ({
                      date: i.date,
                      heading: i.heading,
                      subHeading: i.subHeading,
                      textBlock: i.text,
                    }))}
                  />
                </div>
              </div>
            </div>
          );
        }

        case "element.background-section":
          return (
            <BackgroundSection
              key={index}
              image={item.image}
              heading={item.heading}
              textBlock={item.textBlock}
            />
          );

        case "element.image-text-columns":
          return (
            <ImageTextColumns
              key={index}
              image={item.image}
              superHeading={item.superHeading}
              heading={item.heading}
              textBlock={item.textBlock}
            />
          );

        default:
          return null;
      }
    },
    [vimeoImageMap, photos, handleImageClick]
  );

  const handleScrollToTop = useCallback(() => setToTop(1), []);

  return (
    <>
      <PageHelmet pageTitle="Case Details" />
      <Header stickyNav={false} />

      <BreadcrumbCases
        bgCaseImg={mediaUrl(imgUrl)}
        subHeading={caseName?.subHeading}
        caseName={caseName?.name}
        dataOverlay={5}
        toTop={toTop}
        setToTop={setToTop}
      />

      <div
        id="chapters"
        className={`${containerCssClasses.baseClass} ${containerCssClasses.darkClass}`}
      >
        <ChapterButtons
          activeContainer={activeContainer}
          activateContainer={activateContainer}
          caseName={caseName}
        />

        {/* The chapters arrive on their own request, after the title and
            background image are already on screen. */}
        {isCaseLoading && !container && (
          <div className="container text-center ptb--60">
            <p>Loading…</p>
          </div>
        )}

        {caseError && !container && (
          <div className="container text-center ptb--60">
            <p>This case could not be loaded. Please try again.</p>
          </div>
        )}

        {container?.map(renderComponent)}

        {explorerChapter
          && (explorerVisited || activeContainer === explorerChapter.index)
          && (
            <div
              style={activeContainer === explorerChapter.index
                ? undefined : { display: "none" }}
            >
              <EvidenceExplorerBlock heading={explorerChapter.heading} />
            </div>
          )}

        {mapBlock && (
          <MapTwo
            mapDataArray={mapBlock.map}
            section={caseName?.name || ""}
          />
        )}

        <ChapterButtons
          activeContainer={activeContainer}
          activateContainer={activateContainer}
          caseName={caseName}
        />
      </div>

      {selectedVideoUrl && (
        <VideoModal
          show={isModalOpen}
          onClose={closeModal}
          imageSrc={selectedImage}
          url={selectedVideoUrl}
          description={selectedVideoDescription}
        />
      )}

      <div
        className="backto-top"
        onClick={handleScrollToTop}
        onKeyDown={handleScrollToTop}
        role="button"
        tabIndex={0}
        aria-label="Button to top"
      >
        <ScrollToTop showUnder={160}>
          <FiChevronUp />
        </ScrollToTop>
      </div>

      <FooterTwo setToTop={setToTop} toTop={toTop} />
    </>
  );
};

export default CaseDetails;
